/**
 * Alert Detail Modal Component - Compact Version
 *
 * Displays alert details with expandable task section
 */

import React, { useState, useEffect } from 'react';
import {
  alertAPI,
  taskAPI,
  AlertDetail,
  AlertSeverity,
  AlertStatus,
  AlertType,
  NoActionReason,
  TaskSummary,
} from '../services/api';

const SEVERITY_STYLES: Record<AlertSeverity, { bg: string; text: string; border: string; icon: string; label: string }> = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-400 dark:border-red-600',
    icon: '🔴',
    label: 'Critical',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-400 dark:border-yellow-600',
    icon: '🟡',
    label: 'Warning',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-400 dark:border-blue-600',
    icon: '🔵',
    label: 'Info',
  },
};

const STATUS_LABELS: Record<AlertStatus, string> = {
  unacknowledged: 'Unacknowledged',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
};

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  high_scrap: 'High Scrap',
  high_downtime: 'High Downtime',
  low_efficiency: 'Low Efficiency',
  no_data: 'No Data',
};

const NO_ACTION_REASON_LABELS: Record<NoActionReason, string> = {
  resolved_on_shift: 'Resolved on shift',
  monitoring: 'Monitoring / expected recovery',
  needs_review: 'Unsure - needs review',
  other: 'Other',
};

const TASK_STATUS_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  reassigned: 'Reassigned',
};

interface AlertDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  alertId: number | null;
}

const AlertDetailModal: React.FC<AlertDetailModalProps> = ({ isOpen, onClose, alertId }) => {
  const [alert, setAlert] = useState<AlertDetail | null>(null);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTasks, setShowTasks] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);

  useEffect(() => {
    if (isOpen && alertId) {
      // Reset state when opening for a new alert
      setAlert(null);
      setTasks([]);
      setShowTasks(false);
      fetchAlert();
    }
  }, [isOpen, alertId]);

  const fetchAlert = async () => {
    if (!alertId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await alertAPI.get(alertId);
      setAlert(data);

      // If there are tasks, fetch them
      if (data.task_count > 0) {
        setTasksLoading(true);
        try {
          const taskResponse = await taskAPI.list({ alert_id: alertId, limit: 10, include_completed: true });
          console.log('Fetched tasks for alert', alertId, ':', taskResponse);
          setTasks(taskResponse.items || []);
        } catch (err) {
          console.error('Failed to fetch tasks:', err);
          setTasks([]);
        } finally {
          setTasksLoading(false);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch alert:', err);
      setError(err.response?.data?.detail || 'Failed to load alert details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatKpiValue = (value: number | string | null, kpiName: string): string => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return String(value);
    if (kpiName.toLowerCase().includes('downtime')) {
      return Math.round(numValue).toString();
    }
    return numValue.toFixed(2);
  };

  if (!isOpen) return null;

  const style = alert ? SEVERITY_STYLES[alert.severity] : SEVERITY_STYLES.info;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
        style={{ background: 'var(--bg-secondary)' }}
      >
        {/* Header */}
        <div className={`px-4 py-3 border-b ${style.bg}`} style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>{style.icon}</span>
              <span className={`font-bold ${style.text}`}>Alert #{alertId}</span>
            </div>
            <button
              onClick={onClose}
              className="text-xl hover:opacity-70"
              style={{ color: 'var(--text-secondary)' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-6 text-red-600">{error}</div>
          ) : alert ? (
            <>
              {/* Type & Status */}
              <div className={`p-3 rounded border ${style.bg} ${style.border}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-bold ${style.text}`}>
                    {ALERT_TYPE_LABELS[alert.alert_type]}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    alert.status === 'resolved'
                      ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                      : alert.status === 'acknowledged'
                      ? 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                      : 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200'
                  }`}>
                    {STATUS_LABELS[alert.status]}
                  </span>
                </div>
                {alert.description && (
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    {alert.description}
                  </p>
                )}
              </div>

              {/* KPI & Location - Compact Grid */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 rounded" style={{ background: 'var(--bg-primary)' }}>
                  <div className="text-xs uppercase" style={{ color: 'var(--text-secondary)' }}>KPI</div>
                  <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {alert.kpi_name}: {formatKpiValue(alert.kpi_value, alert.kpi_name)}
                  </div>
                  {alert.threshold_value !== null && (
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Threshold: {formatKpiValue(alert.threshold_value, alert.kpi_name)}
                    </div>
                  )}
                </div>
                <div className="p-2 rounded" style={{ background: 'var(--bg-primary)' }}>
                  <div className="text-xs uppercase" style={{ color: 'var(--text-secondary)' }}>Location</div>
                  <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {alert.department_name || '-'}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {alert.shift_name && `${alert.shift_name} • `}{alert.machine_name || ''}
                  </div>
                </div>
              </div>

              {/* Timeline - Compact */}
              <div className="text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
                <div>Created: {formatDateTime(alert.created_at)}</div>
                {alert.acknowledged_at && (
                  <div>
                    ACK: {formatDateTime(alert.acknowledged_at)}
                    {alert.acknowledged_by_name && ` by ${alert.acknowledged_by_name}`}
                  </div>
                )}
              </div>

              {/* Action Decision - Compact */}
              {alert.action_needed !== null && (
                <div className="p-2 rounded text-sm" style={{ background: 'var(--bg-primary)' }}>
                  {alert.action_needed ? (
                    <span className="text-green-600 dark:text-green-400">✓ Task created</span>
                  ) : (
                    <div>
                      <span className="text-gray-500">✗ No action: </span>
                      <span style={{ color: 'var(--text-primary)' }}>
                        {alert.no_action_reason ? NO_ACTION_REASON_LABELS[alert.no_action_reason] : '-'}
                      </span>
                      {alert.resolution_notes && (
                        <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                          {alert.resolution_notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Pending Action indicator */}
              {alert.status === 'acknowledged' && alert.action_needed === null && (
                <div className="p-2 rounded bg-orange-100 dark:bg-orange-900/30 border border-orange-400">
                  <span className="text-sm text-orange-700 dark:text-orange-300">
                    ⏳ Pending action decision
                  </span>
                </div>
              )}

              {/* Tasks Section - Expandable */}
              {alert.task_count > 0 && (
                <div className="border rounded" style={{ borderColor: 'var(--border-color)' }}>
                  <button
                    onClick={() => setShowTasks(!showTasks)}
                    className="w-full px-3 py-2 flex items-center justify-between text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <span>Tasks ({alert.task_count})</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showTasks ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showTasks && (
                    <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                      {tasksLoading ? (
                        <div className="px-3 py-4 text-center">
                          <div className="animate-spin inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        </div>
                      ) : tasks.length > 0 ? (
                        tasks.map((task) => (
                          <div
                            key={task.id}
                            className="px-3 py-2 border-b last:border-b-0 text-sm"
                            style={{ borderColor: 'var(--border-color)' }}
                          >
                            <div className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                              {task.title}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                              <span>Due: {formatDate(task.due_date)}</span>
                              <span className={`px-1.5 py-0.5 rounded ${
                                task.status === 'completed'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                  : task.status === 'in_progress'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {TASK_STATUS_LABELS[task.status] || task.status}
                              </span>
                              <span>{task.assigned_to_name || 'Unassigned'}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-3 text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                          No tasks found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-end" style={{ borderColor: 'var(--border-color)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded"
            style={{
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;
